import globals from "@/globals";
import * as signals from "@/signals";
import {
	ApiKeyInfo,
	CommonResponse,
	DynamicInfo,
	RealIdLevel,
	SettingsInfo,
	SiteContent,
	UploadResponse,
	UploadSiteRequest,
} from "@/types";
import { closeDialog, encodeData, getRthCdnBaseUrl, showDialog } from "@/utils";
import { t } from "i18next";

interface AnalyticsResponse extends CommonResponse {
	result?: {
		today: {
			desktop?: number;
			mobile?: number;
			now: string;
			pv: number;
			uv: number;
		};
		yesterday: {
			pv: number;
			uv: number;
		};
		month: {
			now: string;
			pv: number;
			uv: number;
		};
	};
}

interface CheckBrowserResponse {
	description: string;
	grade: number;
	isBadDns: boolean;
	isBrowser: boolean;
}

interface ProfileResponse {
	phone: string;
	realIdLevel: RealIdLevel;
	realName: string;
	region: string;
	regTime?: {
		details?: string;
		time?: string;
	};
	tfa: boolean;
}

const AUTH_PREFIX = "Bearer ";
const ERRORS = {
	noData: "No data",
	notLoggedIn: "Not logged in",
};

const backend = globals.BACKEND;
const backendOverseas = globals.BACKEND_OVERSEAS;
const fetchGetInit = {
	credentials: "include",
} as RequestInit;

export async function applySslCertificate(options: {
	domain: string;
	orderUrl: string;
}): Promise<
	CommonResponse & {
		canRetry: boolean;
	}
> {
	const { domain, orderUrl } = options;
	if (!signals.login.value.username) {
		throw new Error(ERRORS.notLoggedIn);
	}
	const response = await fetch(
		backendOverseas +
			"/host/v2/" +
			encodeURIComponent(signals.login.value.username) +
			"/site/" +
			encodeURIComponent(domain) +
			"/ssl/auto",
		{
			body: JSON.stringify({
				orderUrl: orderUrl,
			}),
			credentials: "include",
			method: "PUT",
		},
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
	return await response.json();
}

export async function checkBrowser(): Promise<CheckBrowserResponse> {
	const response = await fetch(
		"https://check.retiehe.com/?" +
			encodeData({
				format: "json",
			}),
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
	return await response.json();
}

export async function createApiKey(): Promise<ApiKeyInfo> {
	if (!signals.login.value.username) {
		throw new Error(ERRORS.notLoggedIn);
	}
	const response = await fetch(
		backend +
			"/api-key/v1/" +
			encodeURIComponent(signals.login.value.username),
		{
			headers: {
				Authorization: AUTH_PREFIX + signals.login.value.token,
			},
			method: "POST",
		},
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
	return await response.json();
}

export async function createSite(
	domain: string,
	oldDomain: string,
	onPaymentRequiredError: () => void | Promise<void>,
): Promise<
	CommonResponse & {
		domain?: string;
	}
> {
	if (!signals.login.value.username) {
		throw new Error(ERRORS.notLoggedIn);
	}
	const response = await fetch(
		backendOverseas +
			"/host/v2/" +
			encodeURIComponent(signals.login.value.username) +
			"/site/" +
			encodeURIComponent(domain),
		{
			body: JSON.stringify({
				oldDomain: oldDomain,
				privilege: globals.privilege || undefined,
			}),
			credentials: "include",
			method: "POST",
		},
	);
	if (!response.ok) {
		if (response.status === 402) {
			void onPaymentRequiredError();
			throw new Error();
		}
		throw new Error(response.status.toString());
	}
	return await response.json();
}

export async function createSslCertificate(domain: string): Promise<
	CommonResponse & {
		orderUrl?: string;
	}
> {
	if (!signals.login.value.username) {
		throw new Error(ERRORS.notLoggedIn);
	}
	const response = await fetch(
		backendOverseas +
			"/host/v2/" +
			encodeURIComponent(signals.login.value.username) +
			"/site/" +
			encodeURIComponent(domain) +
			"/ssl/auto",
		{
			body: JSON.stringify({
				privilege: globals.privilege || undefined,
			}),
			credentials: "include",
			method: "POST",
		},
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
	return await response.json();
}

async function decodeStream(
	response: Response,
	callback?: (response: string) => void,
): Promise<string> {
	if (!response.body) {
		throw new Error(t("error") + response.status.toString());
	}
	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let message = "";
	while (true) {
		const { done, value } = await reader.read();
		if (done) {
			break;
		}
		message += decoder.decode(value);
		callback?.(message);
	}
	return message;
}

export async function deleteApiKey(id: string): Promise<void> {
	if (!signals.login.value.username) {
		throw new Error(ERRORS.notLoggedIn);
	}
	const response = await fetch(
		backend +
			"/api-key/v1/" +
			encodeURIComponent(signals.login.value.username) +
			"/" +
			id,
		{
			headers: {
				Authorization: AUTH_PREFIX + signals.login.value.token,
			},
			method: "DELETE",
		},
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
}

export async function deleteSite(domain: string): Promise<void | {
	alert?: string;
}> {
	if (!signals.login.value.username) {
		throw new Error(ERRORS.notLoggedIn);
	}
	const response = await fetch(
		backendOverseas +
			"/host/v2/" +
			encodeURIComponent(signals.login.value.username) +
			"/site/" +
			encodeURIComponent(domain),
		{
			headers: {
				Authorization: AUTH_PREFIX + signals.login.value.token,
			},
			method: "DELETE",
		},
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
	if (response.status === 204) {
		return;
	}
	return await response.json();
}

export async function deleteSslCertificate(
	domain: string,
): Promise<CommonResponse> {
	if (!signals.login.value.username) {
		throw new Error(ERRORS.notLoggedIn);
	}
	const response = await fetch(
		backendOverseas +
			"/host/v2/" +
			encodeURIComponent(signals.login.value.username) +
			"/site/" +
			encodeURIComponent(domain) +
			"/ssl",
		{
			credentials: "include",
			method: "DELETE",
		},
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
	return await response.json();
}

export async function disableApiKey(id: string): Promise<void> {
	if (!signals.login.value.username) {
		throw new Error(ERRORS.notLoggedIn);
	}
	const response = await fetch(
		backend +
			"/api-key/v1/" +
			encodeURIComponent(signals.login.value.username) +
			"/" +
			id +
			"/disable",
		{
			headers: {
				Authorization: AUTH_PREFIX + signals.login.value.token,
			},
			method: "PUT",
		},
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
}

export async function enableApiKey(id: string): Promise<void> {
	if (!signals.login.value.username) {
		throw new Error(ERRORS.notLoggedIn);
	}
	const response = await fetch(
		backend +
			"/api-key/v1/" +
			encodeURIComponent(signals.login.value.username) +
			"/" +
			id +
			"/enable",
		{
			headers: {
				Authorization: AUTH_PREFIX + signals.login.value.token,
			},
			method: "PUT",
		},
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
}

export async function getAnalytics(domain: string): Promise<AnalyticsResponse> {
	if (!signals.login.value.username) {
		throw new Error(ERRORS.notLoggedIn);
	}
	const response = await fetch(
		backendOverseas +
			"/host/v2/" +
			encodeURIComponent(signals.login.value.username) +
			"/analytics/" +
			encodeURIComponent(domain),
		fetchGetInit,
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
	return await response.json();
}

export async function getApiKeys(): Promise<{
	results: ApiKeyInfo[];
}> {
	if (!signals.login.value.username) {
		throw new Error(ERRORS.notLoggedIn);
	}
	const response = await fetch(
		backend +
			"/api-key/v1/" +
			encodeURIComponent(signals.login.value.username),
		{
			headers: {
				Authorization: AUTH_PREFIX + signals.login.value.token,
			},
		},
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
	return await response.json();
}

export async function getBinaryFile(
	domain: string,
	filename: string,
): Promise<ArrayBuffer> {
	const response = await fetch(
		getRthCdnBaseUrl() +
			"uncached-" +
			Date.now() +
			"/" +
			domain +
			"/" +
			filename,
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
	return await response.arrayBuffer();
}

export async function getBlockStatus(): Promise<{
	isBlocked?: boolean;
	redirect?: string;
}> {
	if (!signals.login.value.username) {
		throw new Error(ERRORS.notLoggedIn);
	}
	const response = await fetch(
		backend +
			"/status/" +
			encodeURIComponent(signals.login.value.username) +
			"?" +
			encodeData({
				token: signals.login.value.token,
			}),
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
	return await response.json();
}

export async function getExpirationTime(): Promise<{
	encrypted?: string;
	time?: number;
}> {
	if (!signals.login.value.username) {
		throw new Error(ERRORS.notLoggedIn);
	}
	const response = await fetch(
		backend +
			"/expiration-time/v1/" +
			encodeURIComponent(signals.login.value.username) +
			"/host",
		{
			headers: {
				Authorization: AUTH_PREFIX + signals.login.value.token,
			},
		},
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
	return await response.json();
}

export async function getInit(): Promise<DynamicInfo> {
	const response = await fetch(
		backend +
			"/init?" +
			encodeData({
				appname: globals.APP_NAME,
				time: Date.now().toString(),
			}),
		fetchGetInit,
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
	return await response.json();
}

export async function getIntro(): Promise<string> {
	const response = await fetch("/intro.txt");
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
	const data = await response.text();
	if (!data) {
		throw new Error(ERRORS.noData);
	}
	return data;
}

export async function getIntroForLoggedIn(): Promise<string> {
	const response = await fetch("/intro-loggedin.txt");
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
	const data = await response.text();
	if (!data) {
		throw new Error(ERRORS.noData);
	}
	return data;
}

function getPostData(
	data: Record<string, unknown>,
	isJson = false,
	method: "POST" | "PUT" | "DELETE" = "POST",
	credentials = true,
): RequestInit {
	let formData: string | FormData;
	if (isJson) {
		formData = JSON.stringify(data);
	} else {
		formData = new FormData();
		for (const key in data) {
			const value = data[key];
			if (!value && value !== 0) {
				continue;
			}
			if (typeof value === "number") {
				formData.append(key, value.toString());
				continue;
			}
			if (typeof value === "string" || value instanceof File) {
				formData.append(key, value);
			}
		}
	}
	return {
		body: formData,
		credentials: credentials ? "include" : "omit",
		headers: isJson ? { "Content-Type": "application/json" } : undefined,
		method: method,
	};
}

export async function getRealId(): Promise<ProfileResponse> {
	if (!signals.login.value.username) {
		throw new Error(ERRORS.notLoggedIn);
	}
	const response = await fetch(
		backend +
			"/profile/v2/" +
			encodeURIComponent(signals.login.value.username) +
			"/realId",
		{
			headers: {
				Authorization: AUTH_PREFIX + signals.login.value.token,
			},
		},
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
	return await response.json();
}

export async function getSettings(): Promise<Partial<SettingsInfo>> {
	const response = await fetch(
		backend +
			"/userdata/set?" +
			encodeData({
				appname: globals.APP_NAME,
				token: signals.login.value.token,
				username: signals.login.value.username,
			}),
		fetchGetInit,
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
	return await response.json();
}

export async function getSiteContent(domain: string): Promise<{
	alert?: string;
	result?: SiteContent;
}> {
	if (!signals.login.value.username) {
		throw new Error(ERRORS.notLoggedIn);
	}
	const response = await fetch(
		backendOverseas +
			"/host/v2/" +
			encodeURIComponent(signals.login.value.username) +
			"/site/" +
			encodeURIComponent(domain) +
			"/content",
		fetchGetInit,
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
	return await response.json();
}

export async function getSiteList(): Promise<{
	results: string[];
}> {
	if (!signals.login.value.username) {
		throw new Error(ERRORS.notLoggedIn);
	}
	const response = await fetch(
		backendOverseas +
			"/host/v2/" +
			encodeURIComponent(signals.login.value.username) +
			"?" +
			encodeData({
				find: globals.params.find, // optional
			}),
		fetchGetInit,
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
	return await response.json();
}

export async function handleApiError(error: unknown): Promise<void> {
	console.error(error);
	if (!error || !navigator.cookieEnabled) {
		return;
	}
	await closeDialog();
	signals.isLoadingShown.value = false;
	if (isUnknownError(error)) {
		void showDialog(t("unknownErrorEncountered"));
	} else if (error instanceof Error && error.message) {
		if (error.message === "403") {
			void showDialog(t("sessionExpired"));
		} else {
			void showDialog(t("error") + error.message);
		}
	}
}

export async function importSite(
	content: SiteContent,
	domain: string,
): Promise<void> {
	if (!signals.login.value.username) {
		throw new Error(ERRORS.notLoggedIn);
	}
	const response = await fetch(
		backendOverseas +
			"/host/v2/" +
			encodeURIComponent(signals.login.value.username) +
			"/site/" +
			encodeURIComponent(domain) +
			"/content",
		getPostData({
			content: JSON.stringify(content),
			erase: "1",
			privilege: globals.privilege || undefined,
		}),
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
}

function isUnknownError(error: unknown): boolean {
	return error instanceof Error && error.message === "Failed to fetch";
}

export async function logOut(): Promise<void> {
	const response = await fetch(
		backend +
			"/logout?" +
			encodeData({
				token: signals.login.value.token,
			}),
		fetchGetInit,
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
}

export async function sendMessageToAi(options: {
	callback: (response: string) => void;
	message: string;
	mode: "host" | "host-open";
}): Promise<string> {
	if (!signals.login.value.username) {
		throw new Error(ERRORS.notLoggedIn);
	}
	const response = await fetch(
		backend +
			"/ai/v1/" +
			encodeURIComponent(signals.login.value.username) +
			"/" +
			options.mode,
		{
			body: JSON.stringify({
				messages: [
					{
						content: options.message,
						role: "user",
					},
				],
			}),
			headers: {
				Authorization: AUTH_PREFIX + signals.login.value.token,
			},
			method: "POST",
		},
	);
	if (!response.body) {
		throw new Error(t("error") + response.status.toString());
	}
	return await decodeStream(response, options.callback);
}

export async function setSettings(key: string, value: string): Promise<void> {
	const response = await fetch(
		backend + "/userdata/set",
		getPostData(
			{
				appname: globals.APP_NAME,
				key: key,
				token: signals.login.value.token,
				username: signals.login.value.username,
				value: value,
			},
			true,
		),
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
}

export async function uploadSite(
	domain: string,
	postData: UploadSiteRequest,
): Promise<UploadResponse> {
	if (!signals.login.value.username) {
		throw new Error(ERRORS.notLoggedIn);
	}
	const response = await fetch(
		backendOverseas +
			"/host/v2/" +
			encodeURIComponent(signals.login.value.username) +
			"/site/" +
			encodeURIComponent(domain) +
			"/content",
		getPostData(postData),
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
	return await response.json();
}

export async function uploadSslCertificate(options: {
	domain: string;
	privateKey: string;
	publicKey: string;
}): Promise<CommonResponse> {
	const { domain, privateKey, publicKey } = options;
	if (!signals.login.value.username) {
		throw new Error(ERRORS.notLoggedIn);
	}
	const response = await fetch(
		backendOverseas +
			"/host/v2/" +
			encodeURIComponent(signals.login.value.username) +
			"/site/" +
			encodeURIComponent(domain) +
			"/ssl/manual",
		getPostData({
			domain: domain,
			privateKey: privateKey,
			privilege: globals.privilege || undefined,
			publicKey: publicKey,
		}),
	);
	if (!response.ok) {
		throw new Error(response.status.toString());
	}
	return await response.json();
}
